import { useQuery } from '@tanstack/react-query';
import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
} from 'chart.js';
import { useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { Line } from 'react-chartjs-2';
import { FaUserCircle, FaCopy } from 'react-icons/fa';
import CustomSelect from '../../Components/CustomSelect';
import withModal from '../../HOC/withModal';
import { usePageTitle } from '../../Hooks/usePageTitle';
import { lineGraphOptions } from '../../Mocks/MockData';
import { getAffiliateDashboard, getAffiliateChart } from '../../Services/Affiliate/Dashboard';
import { showToast } from '../../Components/Toast/toast';
import './Dashboard.css';

ChartJS.register(
  ArcElement,
  Title,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Filler
);
const Dashboard = ({ showModal }) => {
  usePageTitle('Dashboard');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const imageBaseUrl = import.meta.env.VITE_REACT_APP_IMAGE_URL;

  // Generate year options (current year and past 5 years)
  const yearOptions = Array.from({ length: 6 }, (_, i) => {
    const year = new Date().getFullYear() - i;
    return { value: year.toString(), label: year.toString() };
  });

  // Get Dashboard Data
  const { data: dashboardData, isLoading: dashboardLoading } = useQuery({
    queryKey: ['affiliateDashboard'],
    queryFn: getAffiliateDashboard,
    refetchOnWindowFocus: false,
    retry: 1,
  });

  // Get Chart Data
  const { data: chartData, isLoading: chartLoading } = useQuery({
    queryKey: ['affiliateChart', selectedYear],
    queryFn: () => getAffiliateChart(selectedYear),
    refetchOnWindowFocus: false,
    retry: 1,
  });

  const handleYearChange = (e) => {
    setSelectedYear(parseInt(e.target.value));
  };

  const handleCopyReferralCode = () => {
    const referralCode = dashboardData?.user?.referral_code || dashboardData?.user?.affiliate_referral_code;
    if (referralCode) {
      navigator.clipboard.writeText(referralCode)
        .then(() => {
          showToast('Referral code copied to clipboard!', 'success');
        })
        .catch(() => {
          showToast('Failed to copy referral code', 'error');
        });
    }
  };

  // Prepare chart data
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec'];
  const monthlyEarnings = chartData?.monthly_earnings || [];

  const chartDataConfig = {
    labels: months,
    datasets: [
      {
        label: 'Commissions',
        data: monthlyEarnings,
        borderColor: '#5494DA',
        backgroundColor: 'rgba(84, 148, 218, 0.1)',
        borderWidth: 2,
        tension: 0.4,
        fill: true,
        pointRadius: 0,
        pointHoverRadius: 6,
        pointHoverBorderWidth: 3,
        pointBackgroundColor: '#fff',
        pointBorderColor: '#5494DA',
      },
    ],
  };

  const chartOptions = {
    ...lineGraphOptions,
    scales: {
      ...lineGraphOptions.scales,
      y: {
        ...lineGraphOptions.scales.y,
        ticks: {
          ...lineGraphOptions.scales.y.ticks,
          callback: function (value) {
            return '$' + value;
          },
        },
      },
    },
  };

  const user = dashboardData?.user;
  const totalCommission = dashboardData?.totalCommission || 0;
  const monthlyCommission = dashboardData?.monthlyCommission || 0;
  const totalSubscriptions = dashboardData?.totalSubscriptions || 0;

  return (
    <div className="affiliate-dashboard">
      <h2 className="screen-title d-inline-block">User Data</h2>

      {/* User Data Section */}
      <div className="d-card user-data-section mt-4">
        <Row className="align-items-center">
          <Col xs={12} className="mb-3 mb-md-0">
            <div className="avatar-container">
              {user?.image ? (
                <img
                  src={`${imageBaseUrl}${user.image}`}
                  alt={user?.full_name || 'User'}
                  className="user-profile-avatar"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'block';
                  }}
                />
              ) : null}
              <FaUserCircle
                className="user-profile-avatar"
                style={{ display: user?.image ? 'none' : 'block' }}
              />
            </div>
          </Col>
          <Col xs={12} className='mt-4'>
            <Row>
              <Col xs={12} sm={6} md={3} className="mb-3">
                <div className="user-data-item">
                  <label>Affiliate Name</label>
                  <p>{user?.full_name || '-'}</p>
                </div>
              </Col>
              <Col xs={12} sm={6} md={3} className="mb-3">
                <div className="user-data-item">
                  <label>Email Address</label>
                  <p>{user?.email || '-'}</p>
                </div>
              </Col>
              <Col xs={12} sm={6} md={3} className="mb-3">
                <div className="user-data-item">
                  <label>User Type</label>
                  <p>{user?.type || 'Affiliate'}</p>
                </div>
              </Col>
              <Col xs={12} sm={6} md={3} className="mb-3">
                <div className="user-data-item">
                  <label>Total Commission</label>
                  <p>${totalCommission}</p>
                </div>
              </Col>
              <Col xs={12} sm={6} md={3} className="mb-3">
                <div className="user-data-item">
                  <label>This Month's Commission</label>
                  <p>${monthlyCommission}</p>
                </div>
              </Col>
              <Col xs={12} sm={6} md={3} className="mb-3">
                <div className="user-data-item">
                  <label>Total Subscriptions</label>
                  <p>{totalSubscriptions}</p>
                </div>
              </Col>
              <Col xs={12} sm={6} md={3} className="mb-3">
                <div className="user-data-item">
                  <label>Referral Code</label>
                  <div className="referral-code-wrapper">
                    <p
                      className="referral-code-text"
                      onClick={handleCopyReferralCode}
                    >
                      {user?.referral_code || user?.affiliate_referral_code || '-'}
                    </p>
                    <button
                      className="copy-referral-btn"
                      onClick={handleCopyReferralCode}
                      title="Copy referral code"
                    >
                      <FaCopy />
                    </button>
                  </div>
                </div>

              </Col>
            </Row>
          </Col>
        </Row>
      </div>

      {/* Total Commission Chart Section */}
      <div className="d-card commission-chart-section mt-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 className="section-title mb-0">Total Commission</h4>
          <CustomSelect
            name="year"
            options={yearOptions}
            firstIsLabel={false}
            className="gray"
            onChange={handleYearChange}
            value={selectedYear.toString()}
          />
        </div>
        <div style={{ height: 400 }} className="dashboardChart">
          {chartLoading ? (
            <div className="text-center py-5">
              <p className="text-muted">Loading chart...</p>
            </div>
          ) : (
            <Line data={chartDataConfig} options={chartOptions} />
          )}
        </div>
      </div>
    </div>
  );
};

export default withModal(Dashboard);
